import { useEffect, useState } from 'react';

/**
 * How long each phrase holds before the next takes over. Slow enough to read,
 * short enough that a phrase is not still on screen after the work behind it
 * would plausibly have finished.
 */
export const STEP_MS = 1800;

/**
 * What the model is chewing on, while it has sent nothing back yet.
 *
 * The phrases a caller passes are honest about the inputs and dishonest about
 * the timing: they name what the call is actually given, in the order its prompt
 * lists them, but nothing here reports which one it is on. No chat completion
 * emits progress. They are a clock with the right numbers on it, and they earn
 * their place because the alternative is one frozen sentence, or a shimmering
 * skeleton, for however long the draw takes.
 *
 * Holds on the last phrase rather than looping. Coming back round to the first
 * would claim the work started over.
 */
export function useWaitingLabel(labels: readonly string[], active: boolean): string {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!active) {
      setStep(0);
      return;
    }
    const timer = setInterval(
      () => setStep((current) => Math.min(current + 1, labels.length - 1)),
      STEP_MS,
    );
    return () => clearInterval(timer);
    // The length rather than the array: a caller declaring its phrases inline
    // hands over a new identity every render, which would restart the clock on
    // each one and leave it stuck on the first phrase forever.
  }, [active, labels.length]);

  return labels[Math.min(step, labels.length - 1)] ?? '';
}
